import React, { useContext, useEffect, useState, useCallback } from 'react';
import window from 'global/window';
import * as Cookies from 'js-cookie';
import { isEqual } from 'lodash';
import { PageContext } from '../../../contexts/ContextCreator';
import { getCardDimensions, reshapeMatrix } from './components/helpers';
import 'slick-carousel/slick/slick-theme.scss';
import 'slick-carousel/slick/slick.scss';
import { EmitMetric } from '../../Analytics/VisibilityPixel/VisibilityPixel';
import { REGWALL_ADVICE_HUB } from '../../../contexts/Includes/pageContextHelpers';

const AdvicePageContext = React.createContext(undefined);

const AdvicePageProvider = ({
    children,
    contents,
    topicSelected,
    slideSelected = 1,
}) => {
    const [windowWidth, setWindowWidth] = useState(window?.innerWidth);
    const [isSm, setIsSm] = useState(
        window?.innerWidth < 905 && window?.innerWidth >= 600
    );
    const [isXs, setIsXs] = useState(window?.innerWidth < 600);

    const [impressionMetricMap, setImpressionMetricMap] = useState({});

    const windowResize = useCallback(() => {
        setWindowWidth(window?.innerWidth);
    }, []);

    useEffect(() => {
        windowResize();
        window.addEventListener('resize', windowResize, { passive: true });
        return () => {
            window.removeEventListener('resize', windowResize);
        };
    }, [windowResize]);

    const [cardDimensions, setCardDimensions] = useState(
        getCardDimensions(window?.innerWidth)
    );

    useEffect(() => {
        setIsSm(windowWidth < 905);
        setIsXs(windowWidth < 600);
        const _cardDimensions = getCardDimensions(windowWidth);

        if (_cardDimensions?.width !== cardDimensions?.width) {
            setCardDimensions(_cardDimensions);
        }
    }, [windowWidth, cardDimensions]);

    const [communityDiscussion] = useState(
        contents
            ?.filter((c) => c?.type === 'community-discussion')
            .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1))
    );
    const [editorialContent] = useState(
        contents
            ?.filter((c) => c?.type === 'editorial-content')
            .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1))
    );

    const [currentTopic, setCurrentTopic] = useState(topicSelected);

    const initializeVisibleContent = () => {
        let _content = [];
        if (currentTopic) {
            _content = contents
                .filter((t) => t.topic.label === currentTopic)
                .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1));
        } else {
            const _communityDiscussion = communityDiscussion.filter(
                (i) => !i.topic.featured
            );
            const _editorialContent = editorialContent.filter(
                (i) => !i.topic.featured
            );
            _content = _communityDiscussion
                .concat(_editorialContent)
                ?.sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1));
        }

        return _content;
    };

    const [exploreTopics, setExploreTopics] = useState(
        reshapeMatrix(initializeVisibleContent(), window?.innerWidth)
    );

    const initializeCurrentSlide = () => {
        if (
            Number.isInteger(slideSelected) &&
            Math.ceil(exploreTopics.length / 6) >= slideSelected
        ) {
            return Math.abs(slideSelected);
        }
        if (window && window.location && window.history) {
            const url = new URL(window.location);
            const p = Number.parseInt(url.searchParams.get('page'), 10);
            if (p === slideSelected || Number.isNaN(p)) {
                window.history.replaceState(
                    {},
                    '',
                    `/advice${currentTopic ? `/${currentTopic}` : ''}?page=1`
                );
            }
        }
        return 1;
    };
    const [currentSlide, updateCurrentSlide] = useState(
        initializeCurrentSlide()
    );

    useEffect(() => {
        const community = communityDiscussion.filter((i) => !i.topic.featured);
        const editorial = editorialContent.filter((i) => !i.topic.featured);
        let _exploreTopic = community
            .concat(editorial)
            ?.sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1));

        if (!currentTopic && windowWidth < 600) {
            _exploreTopic = contents?.sort((a, b) =>
                a?.topic?.sort > b?.topic?.sort ? 1 : -1
            );
        }

        if (currentTopic) {
            _exploreTopic = contents
                .filter((t) => t.topic.label === currentTopic)
                .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1));
        }
        if (currentTopic === 'featured-content') {
            _exploreTopic = editorialContent
                .filter((i) => i.topic.featured)
                .concat(communityDiscussion.filter((i) => i.topic.featured))
                .sort((a, b) => (a?.topic?.sort > b?.topic?.sort ? 1 : -1));
        }
        const reshaped = reshapeMatrix(_exploreTopic, windowWidth);
        if (!isEqual(reshaped, exploreTopics)) {
            setExploreTopics(reshapeMatrix(_exploreTopic, windowWidth));
        }
    }, [
        currentTopic,
        communityDiscussion,
        editorialContent,
        contents,
        windowWidth,
        exploreTopics,
        slideSelected,
    ]);

    const pageContext = useContext(PageContext);
    const { user } = pageContext;

    const onTopicSelect = ({ topic }) => {
        updateCurrentSlide(1);
        if (topic === currentTopic) {
            window.history.replaceState({}, '', '/advice');
            setCurrentTopic(null);
            return;
        }
        setCurrentTopic(topic);
        if (topic)
            window.history.replaceState(
                {},
                '',
                `/advice${topic ? `/${topic}` : ''}?page=1`
            );
        else window.history.replaceState({}, '', `/advice`);
    };

    const onFireImpression = ({ hash }) => {
        const _metrics = impressionMetricMap;
        _metrics[hash] = true;
        setImpressionMetricMap(_metrics);
    };

    const onDiscussionLinkClick = ({ event, hash, contentUrl }) => {
        EmitMetric({
            click: 1,
            community_content_key: hash,
            // TODO: if we have sponsored company, add company ID
            company_id: [null],
        });
        event.preventDefault();
        Cookies.set('EVENT-LOGIN-TRIGGER', 'advice-hub-registration', {
            expires: 1095,
        });
        let url = contentUrl;
        if (!user) {
            url += `?regwall=${REGWALL_ADVICE_HUB}`;
        }
        window.open(url, '_self');
    };

    return (
        <AdvicePageContext.Provider
            value={{
                communityDiscussion,
                editorialContent,
                content: exploreTopics,
                isLoggedIn: !!user,
                onTopicSelect,
                currentTopic,
                cardDimensions,
                isSm,
                windowWidth,
                updateCurrentSlide,
                currentSlide,
                isXs,
                impressionMetricMap,
                onFireImpression,
                onDiscussionLinkClick,
            }}
        >
            {children}
        </AdvicePageContext.Provider>
    );
};

const useAdvicePage = () => {
    const context = useContext(AdvicePageContext);
    if (context === undefined) {
        throw new Error(
            'useAdvicePage can only be used inside AdvicePageProvider'
        );
    }
    return context;
};

export { AdvicePageProvider, useAdvicePage };
